import { supabase } from '../lib/supabase';

export const currencyAdjustmentsService = {
  // Create currency adjustment
  async createCurrencyAdjustment(adjustmentData) {
    try {
      const { data, error } = await supabase?.from('currency_adjustments')?.insert([adjustmentData])?.select()?.single();

      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      console.error('Create currency adjustment error:', error);
      return { data: null, error: error?.message || 'Failed to create currency adjustment' };
    }
  },

  // Get currency adjustments for organization
  async getCurrencyAdjustments(organizationId, options = {}) {
    try {
      const { page = 1, limit = 20, adjustmentType, dateFrom, dateTo, currency, search } = options;
      const offset = (page - 1) * limit;

      let query = supabase?.from('currency_adjustments')?.select(`
          *,
          created_by:created_by(full_name, email),
          journal_entries:journal_entry_id(entry_number, status)
        `)?.eq('organization_id', organizationId)?.order('created_at', { ascending: false });

      if (adjustmentType) query = query?.eq('adjustment_type', adjustmentType);
      if (dateFrom) query = query?.gte('adjustment_date', dateFrom);
      if (dateTo) query = query?.lte('adjustment_date', dateTo);
      if (currency) query = query?.eq('foreign_currency', currency);
      if (search) {
        query = query?.or(`reference_number.ilike.%${search}%,description.ilike.%${search}%,foreign_currency.ilike.%${search}%`);
      }

      query = query?.range(offset, offset + limit - 1);

      const { data, error } = await query;
      if (error) throw error;

      return { data: data || [], error: null };
    } catch (error) {
      console.error('Get currency adjustments error:', error);
      return { data: [], error: error?.message || 'Failed to fetch currency adjustments' };
    }
  },

  // Update currency adjustment
  async updateCurrencyAdjustment(adjustmentId, updates) {
    try {
      const { data, error } = await supabase?.from('currency_adjustments')?.update({
          ...updates,
          updated_at: new Date()?.toISOString()
        })?.eq('id', adjustmentId)?.select()?.single();

      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      console.error('Update currency adjustment error:', error);
      return { data: null, error: error?.message || 'Failed to update currency adjustment' };
    }
  },

  // Calculate currency revaluation
  async calculateCurrencyRevaluation(amounts, originalRate, newRate) {
    try {
      const baseAmount = parseFloat(amounts?.baseAmount || 0);
      const foreignAmount = parseFloat(amounts?.foreignAmount || 0);
      const oldRate = parseFloat(originalRate);
      const newRateValue = parseFloat(newRate);

      if (oldRate === 0 || newRateValue === 0) {
        throw new Error('Exchange rates cannot be zero');
      }

      // Calculate revaluation adjustment
      const oldBaseValue = foreignAmount * oldRate;
      const newBaseValue = foreignAmount * newRateValue;
      const adjustment = newBaseValue - oldBaseValue;

      // Calculate percentage change
      const percentageChange = ((newRateValue - oldRate) / oldRate) * 100;

      return {
        originalRate: oldRate,
        newRate: newRateValue,
        baseAmount,
        foreignAmount,
        oldBaseValue: parseFloat(oldBaseValue?.toFixed(2)),
        newBaseValue: parseFloat(newBaseValue?.toFixed(2)),
        adjustmentAmount: parseFloat(adjustment?.toFixed(2)),
        percentageChange: parseFloat(percentageChange?.toFixed(4)),
        gainLoss: adjustment > 0 ? 'gain' : adjustment < 0 ? 'loss' : 'neutral',
        error: null
      };
    } catch (error) {
      console.error('Calculate currency revaluation error:', error);
      return { error: error?.message || 'Failed to calculate currency revaluation' };
    }
  },

  // Link journal entry to currency adjustment
  async linkJournalEntry(adjustmentId, journalEntryId) {
    try {
      const { data, error } = await supabase?.from('currency_adjustments')?.update({
          journal_entry_id: journalEntryId,
          updated_at: new Date()?.toISOString()
        })?.eq('id', adjustmentId)?.select()?.single();

      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      console.error('Link journal entry error:', error);
      return { data: null, error: error?.message || 'Failed to link journal entry' };
    }
  },

  // Get currency adjustment by ID
  async getCurrencyAdjustmentById(adjustmentId) {
    try {
      const { data, error } = await supabase?.from('currency_adjustments')?.select(`
          *,
          created_by:created_by(full_name, email),
          journal_entries:journal_entry_id(id, entry_number, status, description)
        `)?.eq('id', adjustmentId)?.single();

      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      console.error('Get currency adjustment error:', error);
      return { data: null, error: error?.message || 'Failed to fetch currency adjustment' };
    }
  },

  // Get currency exposure summary
  async getCurrencyExposureSummary(organizationId, baseCurrency = 'USD') {
    try {
      const { data, error } = await // Current year
      supabase?.from('currency_adjustments')?.select('foreign_currency, amount_in_base_currency, adjustment_amount, adjustment_date')?.eq('organization_id', organizationId)?.eq('base_currency', baseCurrency)?.gte('adjustment_date', new Date(new Date().getFullYear(), 0, 1)?.toISOString()?.split('T')?.[0])?.order('adjustment_date', { ascending: true });

      if (error) throw error;

      // Calculate exposure by currency
      const exposureByCurrency = {};
      const monthlyTrends = {};

      data?.forEach(adjustment => {
        const currency = adjustment?.foreign_currency;
        const month = new Date(adjustment?.adjustment_date)?.toISOString()?.slice(0, 7); // YYYY-MM format
        
        // Currency totals
        if (!exposureByCurrency?.[currency]) {
          exposureByCurrency[currency] = {
            totalExposure: 0,
            totalAdjustments: 0,
            netGainLoss: 0,
            adjustmentCount: 0
          };
        }
        
        exposureByCurrency[currency].totalExposure += parseFloat(adjustment?.amount_in_base_currency || 0);
        exposureByCurrency[currency].totalAdjustments += parseFloat(adjustment?.adjustment_amount || 0);
        exposureByCurrency[currency].netGainLoss += parseFloat(adjustment?.adjustment_amount || 0);
        exposureByCurrency[currency].adjustmentCount++;

        // Monthly trends
        if (!monthlyTrends?.[month]) {
          monthlyTrends[month] = {
            totalAdjustments: 0,
            currencies: new Set()
          };
        }
        monthlyTrends[month].totalAdjustments += parseFloat(adjustment?.adjustment_amount || 0);
        monthlyTrends?.[month]?.currencies?.add(currency);
      });

      // Convert sets to arrays for JSON serialization
      Object.keys(monthlyTrends)?.forEach(month => {
        monthlyTrends[month].currencies = Array.from(monthlyTrends?.[month]?.currencies);
      });

      const summary = {
        baseCurrency,
        totalCurrencies: Object.keys(exposureByCurrency)?.length,
        exposureByCurrency,
        monthlyTrends,
        totalNetGainLoss: Object.values(exposureByCurrency)?.reduce((sum, curr) => sum + curr?.netGainLoss, 0)
      };

      return { data: summary, error: null };
    } catch (error) {
      console.error('Get currency exposure summary error:', error);
      return { data: null, error: error?.message || 'Failed to fetch currency exposure summary' };
    }
  },

  // Delete currency adjustment
  async deleteCurrencyAdjustment(adjustmentId) {
    try {
      const { error } = await supabase?.from('currency_adjustments')?.delete()?.eq('id', adjustmentId);

      if (error) throw error;
      return { error: null };
    } catch (error) {
      console.error('Delete currency adjustment error:', error);
      return { error: error?.message || 'Failed to delete currency adjustment' };
    }
  },

  // Get supported currencies (static list for now)
  getSupportedCurrencies() {
    return [
      { code: 'USD', name: 'US Dollar' },
      { code: 'EUR', name: 'Euro' },
      { code: 'GBP', name: 'British Pound' },
      { code: 'JPY', name: 'Japanese Yen' },
      { code: 'CHF', name: 'Swiss Franc' },
      { code: 'CAD', name: 'Canadian Dollar' },
      { code: 'AUD', name: 'Australian Dollar' },
      { code: 'NZD', name: 'New Zealand Dollar' },
      { code: 'SEK', name: 'Swedish Krona' },
      { code: 'NOK', name: 'Norwegian Krone' },
      { code: 'DKK', name: 'Danish Krone' },
      { code: 'PLN', name: 'Polish Zloty' },
      { code: 'CZK', name: 'Czech Koruna' },
      { code: 'HUF', name: 'Hungarian Forint' },
      { code: 'RUB', name: 'Russian Ruble' },
      { code: 'CNY', name: 'Chinese Yuan' },
      { code: 'INR', name: 'Indian Rupee' },
      { code: 'BRL', name: 'Brazilian Real' },
      { code: 'MXN', name: 'Mexican Peso' },
      { code: 'ZAR', name: 'South African Rand' }
    ];
  }
};